import { useEffect, useState, PropsWithChildren, Children } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { PolicystreamPlan } from '../../../store/policystreamStore';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import { styled } from '@mui/material';
import {
  getPlanPaymentFrequency,
  setPlanPaymentFrequency,
} from '../../../store/policystreamStore';
import { CoreQuotePolicystreamPlanPaymentFrequencyChoices as PolicyStreamPlanPaymentFrequencyChoices } from '../../../gql/graphql';

interface PolicyStreamPlanAmountProps {
  plan: PolicystreamPlan;
  selected: boolean;
}
export const PolicyStreamPlanAmount = ({
  plan,
  selected,
}: PolicyStreamPlanAmountProps) => {
  const selectedPaymentFrequency = useSelector(getPlanPaymentFrequency);
  const dispatch = useDispatch();
  const [saveAmountAnnual, setSaveAmountAnnual] = useState<number>();
  const [saveAmountMonthly, setSaveAmountMonthly] = useState<number>();

  // Compute selected states directly instead of storing in state
  const monthlySelected =
    selectedPaymentFrequency ===
    PolicyStreamPlanPaymentFrequencyChoices.Monthly;
  const annuallySelected =
    selectedPaymentFrequency ===
    PolicyStreamPlanPaymentFrequencyChoices.Annually;

  useEffect(() => {
    // set the savings
    if (plan.premiumMonthlyDollars && plan.premiumAnnualDollars) {
      const monthly = Number(plan.premiumMonthlyDollars);
      const annually = Number(plan.premiumAnnualDollars);
      if (
        [monthly, annually].every(
          (amount) =>
            !Number.isNaN(amount) && Number.isFinite(amount) && amount !== 0,
        )
      ) {
        const monthlyPaymentAnnualized = 12 * monthly;
        const annualPaymentAnnualized = annually;
        // Percentage saved if paying annually (compared to paying monthly)
        const newSaveAmountAnnual =
          (monthlyPaymentAnnualized - annualPaymentAnnualized) /
          monthlyPaymentAnnualized;
        // Percentage saved if paying monthly (compared to paying annually)
        const newSaveAmountMonthly =
          (annualPaymentAnnualized - monthlyPaymentAnnualized) /
          annualPaymentAnnualized;

        setSaveAmountAnnual(newSaveAmountAnnual);
        setSaveAmountMonthly(newSaveAmountMonthly);
      } else {
        // Reset savings if values are invalid
        setSaveAmountAnnual(undefined);
        setSaveAmountMonthly(undefined);
      }
    } else {
      // Reset savings if plan doesn't have both payment options
      setSaveAmountAnnual(undefined);
      setSaveAmountMonthly(undefined);
    }
  }, [plan?.premiumMonthlyDollars, plan?.premiumAnnualDollars]);

  // if we only have annual or monthly payment options, choose that
  useEffect(() => {
    if (plan) {
      if (plan.premiumAnnualDollars && !plan.premiumMonthlyDollars) {
        dispatch(
          setPlanPaymentFrequency(
            PolicyStreamPlanPaymentFrequencyChoices.Annually,
          ),
        );
      } else if (!plan.premiumAnnualDollars && plan.premiumMonthlyDollars) {
        dispatch(
          setPlanPaymentFrequency(
            PolicyStreamPlanPaymentFrequencyChoices.Monthly,
          ),
        );
      }
    }
  }, [plan, dispatch]);

  return (
    <DisplayPlanAmountContainer>
      <DisplayPlanPaymentAmountContainer>
        <EquallySpacedChildren>
          {plan.premiumMonthlyDollars ? (
            <PremiumCard
              selected={selected && monthlySelected}
              isFirst={true}
              isLast={!plan.premiumAnnualDollars}
            >
              <PremiumCardContents>
                {saveAmountMonthly !== undefined && saveAmountMonthly > 0 ? (
                  <SaveAmountContainer>
                    <SaveAmount>
                      Save {(saveAmountMonthly * 100).toFixed(0)}%!
                    </SaveAmount>
                  </SaveAmountContainer>
                ) : (
                  <p></p>
                )}
                <PremiumAmount selected={selected && monthlySelected}>
                  ${plan.premiumMonthlyDollars}
                </PremiumAmount>
                <PremiumFrequency>Monthly</PremiumFrequency>
              </PremiumCardContents>
              {
                // this condition is confusing - basically, we only show this if there's a different option (i.e. if it's only annual no point in showing it - they have to choose annual)
                selected && plan.premiumAnnualDollars ? (
                  <PaymentPlanButtonContainer>
                    <CardActions>
                      <Button
                        disabled={monthlySelected}
                        onClick={() => {
                          dispatch(
                            setPlanPaymentFrequency(
                              PolicyStreamPlanPaymentFrequencyChoices.Monthly,
                            ),
                          );
                        }}
                      >
                        Pay{selected && monthlySelected ? 'ing' : ''} Monthly
                      </Button>
                    </CardActions>
                  </PaymentPlanButtonContainer>
                ) : null
              }
            </PremiumCard>
          ) : null}
          {plan.premiumAnnualDollars ? (
            <PremiumCard
              selected={selected && annuallySelected}
              isFirst={!plan.premiumMonthlyDollars}
              isLast={true}
            >
              <PremiumCardContents>
                {saveAmountAnnual !== undefined && saveAmountAnnual > 0 ? (
                  <SaveAmountContainer>
                    <SaveAmount>
                      Save {(saveAmountAnnual * 100).toFixed(0)}%!
                    </SaveAmount>
                  </SaveAmountContainer>
                ) : (
                  <p></p>
                )}
                <PremiumAmount selected={selected && annuallySelected}>
                  ${plan.premiumAnnualDollars}
                </PremiumAmount>
                <PremiumFrequency>Annually</PremiumFrequency>
              </PremiumCardContents>
              {selected && plan.premiumMonthlyDollars ? (
                <PaymentPlanButtonContainer>
                  <CardActions>
                    <Button
                      disabled={annuallySelected}
                      onClick={() => {
                        dispatch(
                          setPlanPaymentFrequency(
                            PolicyStreamPlanPaymentFrequencyChoices.Annually,
                          ),
                        );
                      }}
                    >
                      Pay{selected && annuallySelected ? 'ing' : ''} Annually
                    </Button>
                  </CardActions>
                </PaymentPlanButtonContainer>
              ) : null}
            </PremiumCard>
          ) : null}
        </EquallySpacedChildren>
      </DisplayPlanPaymentAmountContainer>
    </DisplayPlanAmountContainer>
  );
};

const SaveAmountContainer = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
});

type EquallySpacedChildrenProps = PropsWithChildren<{}>;
const EquallySpacedChildren = ({ children }: EquallySpacedChildrenProps) => {
  return (
    <EqualWidthGrid count={Children.count(children)}>{children}</EqualWidthGrid>
  );
};

interface EqualWidthGridProps {
  count: number;
}
const EqualWidthGrid = styled('div', {
  shouldForwardProp: (prop) => prop !== 'count',
})<EqualWidthGridProps>(({ count }) => {
  return {
    display: 'grid',
    gridTemplateColumns: `repeat(${count}, 1fr)`,
    width: '100%',
  };
});

const PremiumAmount = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'selected',
})<{ selected?: boolean }>(({ theme, selected }) => {
  return {
    fontWeight: 'bold',
    textAlign: 'center',
    fontSize: '1.75rem',
    color: selected ? theme.palette.primary.main : 'inherit',
    [theme.breakpoints.down('md')]: {
      fontSize: '1.5rem',
    },
  };
});

const PaymentPlanButtonContainer = styled('div')(() => {
  return {
    display: 'flex',
    justifyContent: 'center',
  };
});

const PremiumFrequency = styled(Typography)(() => {
  return {
    textTransform: 'lowercase',
    fontSize: '1rem',
    textAlign: 'center',
  };
});

const SaveAmount = styled(Card)(({ theme }) => {
  return {
    display: 'flex',
    color: theme.palette.secondary.contrastText,
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.secondary.dark,
    padding: theme.spacing(0.25, 0.5),
    fontSize: '0.75rem',
  };
});

interface PremiumCardProps {
  selected: boolean;
  isFirst: boolean;
  isLast: boolean;
}
const PremiumCard = styled(Card, {
  shouldForwardProp: (prop) =>
    typeof prop !== 'string' ||
    !['selected', 'isFirst', 'isLast'].includes(prop),
})<PremiumCardProps>(({ selected, isFirst, isLast, theme }) => {
  return {
    borderRadius: `${isFirst ? theme.shape.borderRadius : 0} ${
      isLast ? theme.shape.borderRadius : 0
    } ${isLast ? theme.shape.borderRadius : 0} ${
      isFirst ? theme.shape.borderRadius : 0
    }`,
    border: selected ? `solid 3px ${theme.palette.primary.main}` : undefined,
    display: 'flex',
    flexDirection: 'column',
  };
});
const PremiumCardContents = styled(CardContent)(() => {
  return {
    flexGrow: 1,
    display: 'grid',
    gridTemplateRows: 'repeat(3, 1fr)',
    justifyContent: 'center',
  };
});

const DisplayPlanPaymentAmountContainer = styled('div')(() => {
  return {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'stretch',
    justifyContent: 'flex-start',
    gap: 0,
  };
});

const DisplayPlanAmountContainer = styled('div')(({ theme }) => {
  return {
    padding: theme.spacing(1),
  };
});
